import { useCallback, useContext, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconButton } from 'src/components/Buttons';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { ModalContext } from 'src/contexts/ModalContext/ModalContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { getSalesGroupModalConfigWithDeepLink } from 'src/modals/SalesGroup';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { OpenLinkIcon } from 'src/svgs/OpenLinkIcon';
import { ContentId } from 'src/utils/constants/contentId';
import { isInFullPageEventView } from 'src/utils/deepLinkUtils';
import {
  getSaleActionButton,
  getSaleDetailsDeepLinkUrl,
  getSaleDetailsModalConfigWithDeepLink,
  getSaleDetailsRelativeUrl,
  NON_MAIN_SALE_ACTIONS,
} from 'src/utils/saleUtils';
import {
  Feature,
  Marketplace,
  PointOfSaleSaleStatus,
  SaleActionType,
  SaleDetails,
  TicketType,
} from 'src/WebApiController';

export const SalesActionsCell = ({
  saleId,
  marketplaceSaleId,
  marketplace,
  saleStatus,
  actions,
  ticketType,
  linkOnly,
  isNoFulfill,
  salesGroupId,
}: {
  saleId: number;
  marketplaceSaleId: string | null;
  marketplace: Marketplace | null;
  saleStatus: PointOfSaleSaleStatus;
  actions: SaleActionType[];
  ticketType: TicketType | null;
  linkOnly?: boolean;
  isNoFulfill?: boolean | null;
  /**
   * If the sale belongs to a SalesGroup, this is the group id. When non-null AND
   * the SalesGroupView feature is enabled, the row's view action opens the
   * read-only SalesGroup modal instead of the individual SaleDetailsV2 modal
   * (POS-8632). Sales with a null salesGroupId are unaffected and continue to
   * open SaleDetailsV2.
   */
  salesGroupId?: string | null;
}) => {
  const { setActivePosEntity, isLoading } =
    useActivePosEntityContext<SaleDetails>();
  const { setModal } = useContext(ModalContext);
  const { mainRoute } = useAppContext();
  const navigate = useNavigate();
  const hasSalesGroupViewFeature = useUserHasFeature(Feature.SalesGroupView);

  // POS-8632: a grouped sale opens the SalesGroup modal regardless of whether the
  // host table is `linkOnly` (which forces newTab=true on left-click). We only
  // gate on feature + a non-null group id — NOT on newTab — so `linkOnly` tables
  // don't silently fall through to SaleDetailsV2. Non-grouped sales are untouched.
  const shouldOpenSalesGroupModal = Boolean(
    hasSalesGroupViewFeature && salesGroupId
  );

  const onViewClick = useCallback(
    async (newTab: boolean) => {
      if (shouldOpenSalesGroupModal && salesGroupId) {
        setModal(getSalesGroupModalConfigWithDeepLink(salesGroupId));
        return;
      }

      const inFullPageEventView = isInFullPageEventView(mainRoute);
      if (newTab) {
        window.open(getSaleDetailsDeepLinkUrl(saleId), '_blank');
      } else if (
        mainRoute === MainRoute.Sales ||
        mainRoute === MainRoute.SalesSearch ||
        mainRoute === MainRoute.SalesSearchV2 ||
        mainRoute === MainRoute.Home || // Recent sales table v2
        inFullPageEventView
      ) {
        setActivePosEntity(saleId, marketplaceSaleId, true);
        setModal(getSaleDetailsModalConfigWithDeepLink(saleId, true));
      } else {
        navigate(getSaleDetailsRelativeUrl(saleId));
      }
    },
    [
      shouldOpenSalesGroupModal,
      salesGroupId,
      mainRoute,
      saleId,
      setActivePosEntity,
      marketplaceSaleId,
      setModal,
      navigate,
    ]
  );

  const mainActionTypes = useMemo(
    () => actions.filter((a) => !NON_MAIN_SALE_ACTIONS.includes(a)),
    [actions]
  );

  return (
    <TableCellDiv align="right" showTooltip={false}>
      <Stack direction="row" alignItems="center" gap="m">
        {!linkOnly &&
          (saleStatus === PointOfSaleSaleStatus.PendingConfirmation ||
          saleStatus === PointOfSaleSaleStatus.PendingAllocation ||
          saleStatus === PointOfSaleSaleStatus.Hold ||
          saleStatus === PointOfSaleSaleStatus.CancelledHold ? (
            <>
              {getSaleActionButton(
                saleId,
                marketplaceSaleId,
                marketplace,
                saleStatus,
                mainActionTypes[0],
                ticketType,
                isNoFulfill,
                undefined,
                true,
                undefined,
                isLoading
              )}
              {mainActionTypes.length > 1 &&
                getSaleActionButton(
                  saleId,
                  marketplaceSaleId,
                  marketplace,
                  saleStatus,
                  actions[1],
                  ticketType,
                  isNoFulfill,
                  undefined,
                  true,
                  undefined,
                  isLoading
                )}
            </>
          ) : mainActionTypes.length > 0 ? (
            getSaleActionButton(
              saleId,
              marketplaceSaleId,
              marketplace,
              saleStatus,
              mainActionTypes[mainActionTypes.length - 1],
              ticketType,
              isNoFulfill,
              undefined,
              true,
              undefined,
              isLoading
            )
          ) : null)}
        <Link
          to={getSaleDetailsRelativeUrl(saleId)}
          onClick={(e) => {
            if (e.button === 0 && e.getModifierState('Control') === false) {
              // Perform like a button click when it's a left-click (and no Ctrl)
              // Else the click will be whatever <a /> does
              e.preventDefault();
              e.stopPropagation();

              onViewClick(!!linkOnly);
            }
          }}
        >
          <IconButton
            icon={
              <OpenLinkIcon
                size={vars.iconSize.m}
                withHoverEffect
                fill={vars.color.textBrand}
              />
            }
            titleContentId={ContentId.ViewDetails}
          />
        </Link>
      </Stack>
    </TableCellDiv>
  );
};
